const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface ApiResponse<T> {
  data: T;
  success: true;
}

interface ApiError {
  error: string;
  success: false;
  details?: Record<string, string[]>;
}

type ApiResult<T> = ApiResponse<T> | ApiError;

class ApiClient {
  private baseUrl: string;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAccessToken(): string | null {
    try {
      const stored = localStorage.getItem('yoda-admin-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.accessToken || null;
      }
    } catch {
      return null;
    }
    return null;
  }

  private getRefreshToken(): string | null {
    try {
      const stored = localStorage.getItem('yoda-admin-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.state?.refreshToken || null;
      }
    } catch {
      return null;
    }
    return null;
  }

  private setAccessToken(accessToken: string): void {
    try {
      const stored = localStorage.getItem('yoda-admin-auth');
      if (stored) {
        const parsed = JSON.parse(stored);
        parsed.state.accessToken = accessToken;
        localStorage.setItem('yoda-admin-auth', JSON.stringify(parsed));
      }
    } catch {
      // Ignore errors
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    // If already refreshing, wait for that to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        const result = await response.json();

        if (result.success && result.data?.accessToken) {
          this.setAccessToken(result.data.accessToken);
          return true;
        }
        return false;
      } catch {
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: RequestInit,
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const token = this.getAccessToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      ...options,
    });

    const result: ApiResult<T> = await response.json();

    // If unauthorized and not already a retry, try refreshing the token
    if (!result.success && response.status === 401 && !isRetry && !path.startsWith('/auth/')) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        // Retry the request with the new token
        return this.request<T>(method, path, body, options, true);
      }
    }

    if (!result.success) {
      const error = new Error(result.error) as Error & {
        details?: Record<string, string[]>;
      };
      error.details = result.details;
      throw error;
    }

    return result;
  }

  async get<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>('GET', path, undefined, options);
  }

  async post<T>(
    path: string,
    body?: unknown,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.request<T>('POST', path, body, options);
  }

  async patch<T>(
    path: string,
    body?: unknown,
    options?: RequestInit
  ): Promise<ApiResponse<T>> {
    return this.request<T>('PATCH', path, body, options);
  }

  async delete<T>(path: string, options?: RequestInit): Promise<ApiResponse<T>> {
    return this.request<T>('DELETE', path, undefined, options);
  }

  async upload(path: string, formData: FormData, isRetry = false): Promise<ApiResponse<unknown>> {
    const token = this.getAccessToken();

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers,
      body: formData,
    });

    const result = await response.json();

    // If unauthorized and not already a retry, try refreshing the token
    if (!result.success && response.status === 401 && !isRetry && !path.startsWith('/auth/')) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        // Retry the request with the new token
        return this.upload(path, formData, true);
      }
    }

    if (!result.success) {
      throw new Error(result.error);
    }

    return result;
  }
}

export const api = new ApiClient(API_URL);
